import React, { useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  Chip,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Alert,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import {
  Security as SecurityIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Domain as DomainIcon,
  Router as RouterIcon,
} from '@mui/icons-material';
import { SecuritySettings as SecuritySettingsType, PermissionUtils } from './types';
import { themeBackgrounds } from '../../theme/theme';

interface SecuritySettingsProps {
  securitySettings: SecuritySettingsType;
  setSecuritySettings: React.Dispatch<React.SetStateAction<SecuritySettingsType>>;
  onSave: () => Promise<void>;
  permissionUtils: PermissionUtils;
}

const SecuritySettings: React.FC<SecuritySettingsProps> = ({
  securitySettings,
  setSecuritySettings,
  onSave,
  permissionUtils
}) => {
  const { canEditSection } = permissionUtils;
  const [newDomain, setNewDomain] = useState('');
  const [newIP, setNewIP] = useState('');
  const [domainError, setDomainError] = useState('');
  const [ipError, setIPError] = useState('');

  // Domain validation function
  const validateDomain = (domain: string) => {
    // Remove protocol if present
    let cleanDomain = domain.replace(/^https?:\/\//, '');

    // Check for invalid characters (port numbers, paths, etc.)
    if (cleanDomain.includes(':') || cleanDomain.includes('/')) {
      return false;
    }

    // Basic domain validation
    const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return domainRegex.test(cleanDomain);
  };

  // Validate domain input for real-time feedback
  const validateDomainInput = (domain: string) => {
    if (!domain.trim()) {
      return '';
    }

    const trimmedDomain = domain.trim();

    // Require https:// protocol
    if (!trimmedDomain.startsWith('https://')) {
      return 'Domain must start with https://';
    }

    // Check for port numbers or paths
    if (trimmedDomain.includes(':') && !trimmedDomain.match(/^https?:\/\//)) {
      return 'Port numbers are not allowed. Remove :port from the domain.';
    }

    if (trimmedDomain.includes('/') && !trimmedDomain.match(/^https?:\/\/[^\/]+$/)) {
      return 'Paths are not allowed. Remove everything after the domain name.';
    }

    if (!validateDomain(trimmedDomain)) {
      return 'Please enter a valid domain name. Use format: https://example.com';
    }

    if (securitySettings.allowedDomains.includes(trimmedDomain)) {
      return 'Domain already exists';
    }

    return '';
  };

  // IP validation function
  const validateIP = (ip: string) => {
    const ipv4Regex = /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    const ipv6Regex = /^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/;
    return ipv4Regex.test(ip) || ipv6Regex.test(ip);
  };

  const handleAddDomain = () => {
    if (!newDomain.trim()) {
      setDomainError('Domain cannot be empty');
      return;
    }

    const domain = newDomain.trim();

    // Require https:// protocol
    if (!domain.startsWith('https://')) {
      setDomainError('Domain must start with https://');
      return;
    }

    // Check for port numbers or paths
    if (domain.includes(':') && !domain.match(/^https?:\/\//)) {
      setDomainError('Port numbers are not allowed. Remove :port from the domain.');
      return;
    }

    if (domain.includes('/') && !domain.match(/^https?:\/\/[^\/]+$/)) {
      setDomainError('Paths are not allowed. Remove everything after the domain name.');
      return;
    }

    if (!validateDomain(domain)) {
      setDomainError('Please enter a valid domain name. Use format: https://example.com');
      return;
    }

    if (securitySettings.allowedDomains.includes(domain)) {
      setDomainError('Domain already exists');
      return;
    }

    setSecuritySettings(prev => ({
      ...prev,
      allowedDomains: [...prev.allowedDomains, newDomain.trim()]
    }));
    setNewDomain('');
    setDomainError('');
  };

  const handleAddIP = () => {
    if (!newIP.trim()) {
      setIPError('IP address cannot be empty');
      return;
    }

    if (!validateIP(newIP.trim())) {
      setIPError('Please enter a valid IP address');
      return;
    }

    if (securitySettings.allowedIPs.includes(newIP.trim())) {
      setIPError('IP address already exists');
      return;
    }

    setSecuritySettings(prev => ({
      ...prev,
      allowedIPs: [...prev.allowedIPs, newIP.trim()]
    }));
    setNewIP('');
    setIPError('');
  };

  const handleRemoveDomain = (domain: string) => {
    setSecuritySettings(prev => ({
      ...prev,
      allowedDomains: prev.allowedDomains.filter(d => d !== domain)
    }));
  };

  const handleRemoveIP = (ip: string) => {
    setSecuritySettings(prev => ({
      ...prev,
      allowedIPs: prev.allowedIPs.filter(i => i !== ip)
    }));
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <SecurityIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Security & Privacy Settings</Typography>
          {!canEditSection('security') && (
            <Chip
              label="Read Only"
              color="default"
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </Box>

        <Grid2 container spacing={3}>
          {/* Allowed Domains Section */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <DomainIcon sx={{ mr: 1, color: 'primary.main' }} />
                Allowed Domains
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter domains to allow access to this application (whitelist).
              </Typography>

              {/* Add Domain Input */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Enter domain (e.g., https://www.example.com)"
                  placeholder="https://www.example.com"
                  value={newDomain}
                  onChange={(e) => {
                    setNewDomain(e.target.value);
                    setDomainError('');
                  }}
                  onBlur={() => {
                    const error = validateDomainInput(newDomain);
                    setDomainError(error);
                  }}
                  disabled={!canEditSection('security')}
                  error={!!domainError}
                  helperText={domainError}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAddDomain();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddDomain}
                  disabled={!canEditSection('security')}
                  sx={{
                    minWidth: 'auto', px: 2, height: '40px',
                    background: themeBackgrounds.default,
                    color: '#ffffff',
                    '&:hover': {
                      background: themeBackgrounds.default,
                      opacity: 0.9,
                    },
                  }}
                >
                  Add
                </Button>
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Note:</strong> Domains must start with 'https://' and may include subdomains.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, marginLeft: "-50px", color: 'success.main' }}>
                  <strong>✓ Valid examples:</strong> 'https://www.example.com'
                </Typography>
                <Typography variant="body2" sx={{ color: 'error.main' }}>
                  <strong>✗ Invalid examples:</strong> 'https://www.example.com:8080', 'http://example.com'
                </Typography>
              </Alert>
              {/* Domains List */}
              {securitySettings.allowedDomains.length > 0 ? (
                <List dense sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  {securitySettings.allowedDomains.map((domain, index) => (
                    <ListItem
                      key={index}
                      divider={index < securitySettings.allowedDomains.length - 1}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveDomain(domain)}
                          disabled={!canEditSection('security')}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={domain}
                        secondary={`Domain ${index + 1}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No domains added yet. Add domains to restrict access to your application.
                </Alert>
              )}
            </Box>
          </Grid2>

          {/* Allowed IPs Section */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <RouterIcon sx={{ mr: 1, color: 'primary.main' }} />
                Allowed IPs for API Calls
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Specify IP addresses that are allowed to make API calls
              </Typography>

              {/* Add IP Input */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Enter IP address (IPv4 or IPv6)"
                  value={newIP}
                  onChange={(e) => {
                    setNewIP(e.target.value);
                    setIPError('');
                  }}
                  disabled={!canEditSection('security')}
                  error={!!ipError}
                  helperText={ipError}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAddIP();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddIP}
                  disabled={!canEditSection('security')}
                  sx={{
                    minWidth: 'auto', px: 2, height: '40px',
                    background: themeBackgrounds.default,
                    color: '#ffffff',
                    '&:hover': {
                      background: themeBackgrounds.default,
                      opacity: 0.9,
                    },
                  }}
                >
                  Add
                </Button>
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Note:</strong> Enter valid IPv4 or IPv6 addresses. Do not include port numbers or protocols.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, color: 'success.main' }}>
                  <strong>✓ Valid examples:</strong> '192.168.1.100', '203.0.113.45', '2001:db8::1'
                </Typography>
                <Typography variant="body2" sx={{ color: 'error.main' }}>
                  <strong>✗ Invalid examples:</strong> '192.168.1.100:8080', 'http://192.168.1.100'
                </Typography>
              </Alert>

              {/* IPs List */}
              {securitySettings.allowedIPs.length > 0 ? (
                <List dense sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  {securitySettings.allowedIPs.map((ip, index) => (
                    <ListItem
                      key={index}
                      divider={index < securitySettings.allowedIPs.length - 1}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveIP(ip)}
                          disabled={!canEditSection('security')}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={ip}
                        secondary={`IP Address ${index + 1}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No IP addresses added yet. Add IPs to restrict API access.
                </Alert>
              )}
            </Box>
          </Grid2>

          {/* Preview Section */}
          <Grid2 size={{ xs: 12 }}>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              Security Configuration Preview
            </Typography>

            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Allowed Domains ({securitySettings.allowedDomains.length})
                  </Typography>
                  {securitySettings.allowedDomains.length > 0 ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {securitySettings.allowedDomains.map((domain, index) => (
                        <Chip
                          key={index}
                          label={domain}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No domains configured
                    </Typography>
                  )}
                </Box>
              </Grid2>

              <Grid2 size={{ xs: 12, md: 6 }}>
                <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Allowed IP Addresses ({securitySettings.allowedIPs.length})
                  </Typography>
                  {securitySettings.allowedIPs.length > 0 ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {securitySettings.allowedIPs.map((ip, index) => (
                        <Chip
                          key={index}
                          label={ip}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No IP addresses configured
                    </Typography>
                  )}
                </Box>
              </Grid2>
            </Grid2>
          </Grid2>

          {/* Save Button */}
          <Grid2 size={{ xs: 12 }}>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={onSave}
                disabled={!canEditSection('security')}
                sx={{
                  mr: 1,
                  background: themeBackgrounds.default,
                  color: '#ffffff',
                  '&:hover': {
                    background: themeBackgrounds.default,
                    opacity: 0.9,
                  },
                }}
              >
                Save Security Settings
              </Button>
            </Box>
          </Grid2>
        </Grid2>
      </CardContent>
    </Card>
  );
};

export default SecuritySettings;